import React, { FC } from 'react';

import { useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import { EFeatureFlag, EProgressBarType } from 'constants/enums';
import { PROGRESS_BAR_DEFAULTS } from 'views/OfferDesign/OfferDesign.consts';

import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { ColorPicker } from 'components/ui/ColorPicker/color-picker';

import { useFormFieldsContext } from '../../../context';
import {
  FormFieldsColorRow,
  FormFieldsIdentitySection,
  FormFieldsNumberRow,
  FormFieldsSection,
  FormFieldsTitleStyleSection,
  FormFieldsUploadSection
} from '../components';

import {
  BAR_FILL_COLOR_FIELD,
  BAR_NUMBER_FIELDS,
  BORDER_BACKGROUND_GRADIENT_FIELDS,
  CLASSIC_UPLOAD_FIELDS,
  findProgressBarTypeOption,
  FONT_WEIGHT_OPTIONS,
  FRAME_NUMBER_FIELDS,
  MILESTONE_UPLOAD_FIELDS,
  PROGRESS_BAR_TYPE_OPTIONS,
  PROGRESS_BAR_TYPE_TOOLTIP,
  SUBTITLE_DEFAULTS,
  TITLE_DEFAULTS
} from './ProgressBarFormFields.consts';

export const ProgressBarFormFields: FC = (): React.ReactElement => {
  const { values, setFieldValue } = useFormFieldsContext();
  const { publisherId } = useParams();
  const { getFeatureFlagByKey } = useFeatureFlags({ publisherId });

  const isMilestonesEnabled = Boolean(
    getFeatureFlagByKey(EFeatureFlag.REWARD_PROGRESS_BAR_MILESTONES_ENABLED)
  );

  const progressBarType = values.progressBarType || EProgressBarType.CLASSIC;
  const isMilestone =
    isMilestonesEnabled && progressBarType === EProgressBarType.MILESTONE;
  const uploadFields = isMilestone ? MILESTONE_UPLOAD_FIELDS : CLASSIC_UPLOAD_FIELDS;

  return (
    <React.Fragment>
      <FormFieldsSection
        heading="Progress Bar"
        description="Customize the progress bar to fit your store theme."
      >
        {isMilestonesEnabled && (
          <div className="mb-5">
            <InputSelectDropdown
              label="Progress Bar Type"
              infoTooltipContent={PROGRESS_BAR_TYPE_TOOLTIP}
              isRequired
              options={PROGRESS_BAR_TYPE_OPTIONS}
              defaultSelected={findProgressBarTypeOption(values.progressBarType)}
              onOptionChange={(value) =>
                setFieldValue('progressBarType', value as EProgressBarType)
              }
              buttonDataTestId="offer-design-progress-bar-type-button"
              listDataTestId="offer-design-progress-bar-type-list"
            />
          </div>
        )}
        <FormFieldsIdentitySection spacing="none" />
      </FormFieldsSection>

      <FormFieldsSection
        heading="Progress Bar Frame"
        description="Set your frame settings."
        showSeparator
      >
        <FormFieldsNumberRow fields={FRAME_NUMBER_FIELDS} />
      </FormFieldsSection>

      {isMilestone && (
        <React.Fragment>
          <FormFieldsTitleStyleSection
            heading="Title"
            nameField="title"
            sizeField="fontSize"
            weightField="fontWeight"
            fontWeightOptions={FONT_WEIGHT_OPTIONS}
            fontWeightButtonDataTestId="offer-design-progress-bar-title-font-weight-button"
            fontWeightListDataTestId="offer-design-progress-bar-title-font-weight-list"
            defaults={TITLE_DEFAULTS}
            shouldShowColor={false}
            showSeparator
          />
          <FormFieldsTitleStyleSection
            heading="Subtitle"
            nameField="subTitleText"
            nameLabel="Subtitle"
            sizeField="subTitleFontSize"
            weightField="subTitleFontWeight"
            fontWeightOptions={FONT_WEIGHT_OPTIONS}
            fontWeightButtonDataTestId="offer-design-progress-bar-subtitle-font-weight-button"
            fontWeightListDataTestId="offer-design-progress-bar-subtitle-font-weight-list"
            defaults={SUBTITLE_DEFAULTS}
            shouldShowColor={false}
          />
        </React.Fragment>
      )}

      <FormFieldsSection
        heading="Progress Bar Design"
        description="Set colors and upload assets for your bar."
        showSeparator
      >
        <div className="flex gap-5">
          <div className="flex-1">
            <ColorPicker
              label="Font Color"
              infoTooltipContent="Applies to all text in the Progress Bar."
              data-testid="offer-design-progress-bar-font-color-input"
              defaultValue={values.textColor || PROGRESS_BAR_DEFAULTS.textColor}
              onChange={(color) => setFieldValue('textColor', color)}
            />
          </div>
          <FormFieldsColorRow fields={[BAR_FILL_COLOR_FIELD]} layout="flex" />
        </div>

        <FormFieldsColorRow
          fields={BORDER_BACKGROUND_GRADIENT_FIELDS}
          spacing="sm"
        />

        <FormFieldsNumberRow fields={BAR_NUMBER_FIELDS} spacing="sm" />

        <FormFieldsUploadSection fields={uploadFields} spacing="lg" />
      </FormFieldsSection>
    </React.Fragment>
  );
};
